/**
 * Workload spec utilities + TanStack Query hooks for the live/draft
 * pair the controller persists on every Service.
 *
 * The controller owns both revisions:
 *   - `live`  → ``Service.workload``       (what's running)
 *   - `draft` → ``Service.workload_draft`` (what the operator's editing)
 *
 * Endpoints (see `controller/controller/api/routers/workloads.py`):
 *   - GET  /services/:id/workload?revision=live|draft
 *   - PUT  /services/:id/workload/draft       (save draft, auto-clears
 *                                               if equal to live)
 *   - POST /services/:id/workload/draft/discard
 *   - POST /services/:id/workload/promote     (draft -> live, fired by
 *                                               the rollout button)
 *
 * This module exposes:
 *   - typed query hooks for live/draft
 *   - mutations for save / discard / promote (with cache invalidation)
 *   - pure helpers (`diffWorkloadSpec`, `flattenArgs`, `hashWorkloadSpec`,
 *     `defaultWorkloadSpec`, `normalizeWorkloadSpec`) operating on the
 *     wire `WorkloadSpec` shape from `@/api/types`
 *
 * Drafts are server-authoritative — there's no localStorage cache so
 * what one operator is editing is what teammates see on the same row.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  Service,
  ServiceWorkloadOut,
  WorkloadArg,
  WorkloadEnvVar,
  WorkloadSpec,
} from "@/api/types";

// Re-export the wire types so call sites can keep importing from this
// module (which used to own them) without rewriting every file.
export type {
  ServiceWorkloadOut,
  WorkloadArg,
  WorkloadEnvVar,
  WorkloadSpec,
} from "@/api/types";

export type WorkloadRevision = "live" | "draft";

const liveKey = (serviceId: number) =>
  ["service", serviceId, "workload", "live"] as const;
const draftKey = (serviceId: number) =>
  ["service", serviceId, "workload", "draft"] as const;

/**
 * Fetch the *live* workload for a service. Cheap and shared across
 * pages — TanStack Query dedupes concurrent callers.
 */
export function useLiveWorkload(serviceId: number | undefined) {
  return useQuery({
    queryKey: liveKey(serviceId ?? -1),
    enabled: serviceId !== undefined,
    queryFn: () =>
      api.get<ServiceWorkloadOut>(
        `/services/${serviceId}/workload?revision=live`,
      ),
    staleTime: 10_000,
  });
}

/**
 * Fetch the *draft* workload for a service. The controller falls back
 * to live when no draft is set, and reports `has_pending_changes` so
 * the caller can decide whether to render the diff badge.
 */
export function useDraftWorkload(serviceId: number | undefined) {
  return useQuery({
    queryKey: draftKey(serviceId ?? -1),
    enabled: serviceId !== undefined,
    queryFn: () =>
      api.get<ServiceWorkloadOut>(
        `/services/${serviceId}/workload?revision=draft`,
      ),
    staleTime: 5_000,
  });
}

/**
 * PUT /services/:id/workload/draft — save the operator's pending edits.
 *
 * Pass `ifRevision` (the `draft_revision` the editor was seeded from) and
 * the controller will 412 on concurrent edits — the caller should refetch
 * and offer the operator a "their changes / yours" choice. Omit it for
 * "force-save" flows.
 */
export function useSaveDraftWorkload(serviceId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      spec,
      ifRevision,
    }: {
      spec: WorkloadSpec;
      ifRevision?: number;
    }) =>
      api.put<ServiceWorkloadOut>(
        `/services/${serviceId}/workload/draft`,
        spec,
        ifRevision !== undefined
          ? { headers: { "If-Match": String(ifRevision) } }
          : undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: draftKey(serviceId) });
      // The draft endpoint normalizes draft==live into "no pending"
      // by clearing workload_draft on the service, but doesn't touch
      // the live spec. We still invalidate `services` since the
      // top-level list response ships `workload_draft` as part of
      // ServiceOut.
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
}

/** POST /services/:id/workload/draft/discard — revert draft to live. */
export function useDiscardDraftWorkload(serviceId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ServiceWorkloadOut>(
        `/services/${serviceId}/workload/draft/discard`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: draftKey(serviceId) });
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
}

/**
 * Convenience accessor: revision token off a Service row. Used by the
 * editor to seed `If-Match` on save without hitting `/workload?revision=draft`
 * separately when the row is already in `useServices()` cache.
 */
export function draftRevisionOf(service: Service): number {
  return service.workload_draft_revision ?? 0;
}

// ---- pure helpers -------------------------------------------------------

/**
 * Pull a normalized live spec straight off a Service row (the form
 * `useServices()` already returns). Saves a network round-trip when
 * the parent already has the row in hand and just needs the
 * normalized WorkloadSpec.
 */
export function liveWorkloadOf(service: Service): WorkloadSpec {
  return normalizeWorkloadSpec(service.workload);
}

/**
 * Pull a normalized draft spec off a Service row when the controller
 * has shipped one in `workload_draft`. Returns the live spec when
 * draft is null (= no pending changes), so callers always get a
 * complete spec.
 */
export function draftWorkloadOf(service: Service): WorkloadSpec {
  return normalizeWorkloadSpec(service.workload_draft ?? service.workload);
}

export function hasPendingDraft(service: Service): boolean {
  if (service.workload_draft == null) return false;
  return (
    hashWorkloadSpec(normalizeWorkloadSpec(service.workload_draft)) !==
    hashWorkloadSpec(normalizeWorkloadSpec(service.workload))
  );
}

export function normalizeWorkloadSpec(raw: unknown): WorkloadSpec {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const env: WorkloadEnvVar[] = Array.isArray(r.env)
    ? (r.env as unknown[]).map((e) => {
        const o = (e && typeof e === "object" ? e : {}) as Record<string, unknown>;
        return {
          key: typeof o.key === "string" ? o.key : "",
          value: typeof o.value === "string" ? o.value : "",
          secret: typeof o.secret === "boolean" ? o.secret : false,
        };
      })
    : [];
  const args: WorkloadArg[] = Array.isArray(r.args)
    ? (r.args as unknown[]).map((a) => {
        const o = (a && typeof a === "object" ? a : {}) as Record<string, unknown>;
        return {
          flag: typeof o.flag === "string" ? o.flag : "",
          value: typeof o.value === "string" ? o.value : null,
        };
      })
    : [];
  return {
    image: typeof r.image === "string" && r.image !== "" ? r.image : null,
    command: typeof r.command === "string" && r.command !== "" ? r.command : null,
    args,
    port: typeof r.port === "number" && Number.isFinite(r.port) ? r.port : 8000,
    env,
    health_path: typeof r.health_path === "string" && r.health_path !== ""
      ? r.health_path
      : "/healthz",
    initial_delay_seconds: typeof r.initial_delay_seconds === "number"
      ? r.initial_delay_seconds
      : 60,
    metrics_path: typeof r.metrics_path === "string" && r.metrics_path !== ""
      ? r.metrics_path
      : "/metrics",
    setup: typeof r.setup === "string" && r.setup !== "" ? r.setup : null,
  };
}

/**
 * Initial form values for a service that has never had a workload
 * configured. Pre-filled with the backstage-published
 * ``backstage-loadtest`` image so a freshly created service is
 * immediately deployable end-to-end without the operator having to
 * pick an image first. Replace freely with vLLM, TGI, or any custom
 * inference image — see ``loadtest-server/README.md`` for the contract
 * the default image exposes (``/healthz``, ``/metrics``, GPU-emulator
 * batching) and the controller's
 * ``container_registry_auths`` table for how the private GHCR pull is
 * authenticated.
 *
 * vLLM-flavoured sample (image + ``--model`` args + ``HF_TOKEN``)
 * remains a one-paste swap; the form's placeholders still describe
 * that path so operators who want it can build it inline.
 */
export function defaultWorkloadSpec(): WorkloadSpec {
  return {
    image: "ghcr.io/spiral-ai/backstage-loadtest:latest",
    command: "/usr/local/bin/loadtest-server",
    port: 8000,
    args: [],
    env: [
      { key: "MAX_BATCH", value: "8", secret: false },
      { key: "RTF", value: "0.1", secret: false },
    ],
    health_path: "/healthz",
    initial_delay_seconds: 60,
    metrics_path: "/metrics",
    setup: null,
  };
}

/**
 * Flatten a workload's args list into a posix-style argv (e.g. for
 * previewing what gets passed to the entrypoint). Boolean flags emit
 * just the flag; value-bearing entries emit two tokens. Empty `flag`
 * entries are dropped (they came from a half-edited row in the editor).
 */
export function flattenArgs(args: WorkloadArg[]): string[] {
  const out: string[] = [];
  for (const a of args) {
    const flag = a.flag.trim();
    if (!flag) continue;
    out.push(flag);
    const value = (a.value ?? "").trim();
    if (value !== "") out.push(value);
  }
  return out;
}

/**
 * Stable-ish hash for a workload spec. Used to identify which replicas
 * are running which spec and to detect "draft == live" client-side.
 * Not cryptographic — just a fast canonical-JSON fingerprint for UI
 * bookkeeping.
 */
export function hashWorkloadSpec(w: WorkloadSpec): string {
  const json = JSON.stringify(canonicalize(w));
  let h = 0;
  for (let i = 0; i < json.length; i++) {
    h = (h * 31 + json.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) {
      out[k] = canonicalize(obj[k]);
    }
    return out;
  }
  return value;
}

// ---- diff utilities -----------------------------------------------------

export type WorkloadDiff =
  | { path: "scalar"; field: string; before: string; after: string }
  | { path: "added"; field: string; after: string }
  | { path: "removed"; field: string; before: string };

function fmt(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === "") return "(unset)";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

interface ScalarFieldSpec {
  field: string;
  read: (w: WorkloadSpec) => string | number | null;
}

const SCALAR_FIELDS: ScalarFieldSpec[] = [
  { field: "image", read: (w) => w.image ?? null },
  { field: "command", read: (w) => w.command ?? null },
  { field: "port", read: (w) => w.port },
  { field: "health_path", read: (w) => w.health_path },
  { field: "initial_delay_seconds", read: (w) => w.initial_delay_seconds },
  { field: "metrics_path", read: (w) => w.metrics_path },
];

/**
 * Compute a diff between two workload specs. Treats `args` as a map
 * keyed by `flag` and `env` as a map keyed by `key` so add / remove /
 * change is surfaced cleanly even when entries are reordered.
 *
 * `before === null` is supported (= initial rollout) — every field of
 * `after` lands in the diff as "added".
 */
export function diffWorkloadSpec(
  before: WorkloadSpec | null,
  after: WorkloadSpec,
): WorkloadDiff[] {
  const diffs: WorkloadDiff[] = [];

  if (!before) {
    for (const spec of SCALAR_FIELDS) {
      diffs.push({
        path: "added",
        field: spec.field,
        after: fmt(spec.read(after)),
      });
    }
    for (const a of after.args) {
      diffs.push({
        path: "added",
        field: `args[${a.flag}]`,
        after: fmt(a.value),
      });
    }
    for (const e of after.env) {
      diffs.push({
        path: "added",
        field: `env[${e.key}]`,
        after: e.secret ? "••••••" : fmt(e.value),
      });
    }
    return diffs;
  }

  for (const spec of SCALAR_FIELDS) {
    const av = fmt(spec.read(before));
    const bv = fmt(spec.read(after));
    if (av !== bv) {
      diffs.push({
        path: "scalar",
        field: spec.field,
        before: av,
        after: bv,
      });
    }
  }

  // args
  const beforeArgs = new Map(before.args.map((x) => [x.flag, x.value ?? ""]));
  const afterArgs = new Map(after.args.map((x) => [x.flag, x.value ?? ""]));
  for (const [flag, value] of afterArgs) {
    if (!beforeArgs.has(flag)) {
      diffs.push({ path: "added", field: `args[${flag}]`, after: fmt(value) });
    } else if (beforeArgs.get(flag) !== value) {
      diffs.push({
        path: "scalar",
        field: `args[${flag}]`,
        before: fmt(beforeArgs.get(flag) ?? ""),
        after: fmt(value),
      });
    }
  }
  for (const [flag, value] of beforeArgs) {
    if (!afterArgs.has(flag)) {
      diffs.push({
        path: "removed",
        field: `args[${flag}]`,
        before: fmt(value),
      });
    }
  }

  // env (mask values when secret on either side)
  const beforeEnv = new Map(before.env.map((x) => [x.key, x] as const));
  const afterEnv = new Map(after.env.map((x) => [x.key, x] as const));
  const maskedFmt = (e: WorkloadEnvVar) => (e.secret ? "••••••" : fmt(e.value));
  for (const [k, av] of afterEnv) {
    if (!beforeEnv.has(k)) {
      diffs.push({ path: "added", field: `env[${k}]`, after: maskedFmt(av) });
    } else {
      const bv = beforeEnv.get(k)!;
      if (bv.value !== av.value || bv.secret !== av.secret) {
        diffs.push({
          path: "scalar",
          field: `env[${k}]`,
          before: maskedFmt(bv),
          after: maskedFmt(av),
        });
      }
    }
  }
  for (const [k, bv] of beforeEnv) {
    if (!afterEnv.has(k)) {
      diffs.push({
        path: "removed",
        field: `env[${k}]`,
        before: maskedFmt(bv),
      });
    }
  }

  return diffs;
}

